import type { BadgeMap } from '../../lib/types'
import type {
  PickupOrderRow,
  PickupPaymentPart,
  PickupTemperature,
} from '../../api/use-pickups-query'

/**
 * Fulfilment status as the sheet names it. CANCELLED is absent on purpose — the report never
 * returns cancelled orders, so a badge for it would be dead code that quietly invites one.
 */
export const PICKUP_STATUS_BADGES: BadgeMap = {
  CREATED: { label: 'Neu', variant: 'secondary' },
  PREPARING: { label: 'In Vorbereitung', variant: 'outline' },
  READY: { label: 'Bereit', variant: 'default' },
  PICKED_UP: { label: 'Abgeholt', variant: 'default' },
}

export const GROUP_ORDER_LABEL = 'Gruppenbestellung'

export const MISSING_CODE_LABEL = '—'

export const UNSETTLED_LABEL = 'offen'

/**
 * Warm/cold suffix for a dish line, or empty when the product offers no choice.
 * The server already normalized legacy lines, so null here means "no temperature at all".
 */
export function temperatureSuffix(temperature: PickupTemperature): string {
  if (temperature === 'WARM') return ' (warm)'
  if (temperature === 'COLD') return ' (kalt)'
  return ''
}

/** Who collects this order, or that nobody in particular does. */
export function customerLabel(order: PickupOrderRow): string {
  return order.customerName ?? GROUP_ORDER_LABEL
}

/** Orders created before pickup codes existed still have to print a cell. */
export function pickupCodeLabel(order: PickupOrderRow): string {
  return order.pickupCode ?? MISSING_CODE_LABEL
}

/**
 * One payment as the sheet reads it. A card is only called paid once the money is captured;
 * a repeated voucher type carries its count, so five identical vouchers stay one line.
 */
export function paymentPartLabel(part: PickupPaymentPart): string {
  if (part.kind === 'CARD') {
    return part.captured ? 'Karte — bezahlt' : 'Karte — autorisiert'
  }
  if (part.kind === 'FORKS') {
    return 'Gabeln'
  }
  const label = part.label ?? 'Gutschein'
  return part.count > 1 ? `${label} (${part.count}×)` : label
}

/** All of an order's payments in one cell, or a dash when nothing was ever recorded. */
export function paymentSummary(order: PickupOrderRow): string {
  if (order.payments.length === 0) return MISSING_CODE_LABEL
  return order.payments.map(paymentPartLabel).join(' + ')
}

/** The pickup slot in the report's timezone — not the reader's. */
export function formatPickupTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** The "Stand:" stamp, in the same timezone as the slots above it. */
export function formatGeneratedAt(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('de-AT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** The report date with its weekday, the way the printed sheet titles the day. */
export function formatReportDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Intl.DateTimeFormat('de-AT', {
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(year, month - 1, day))
}

/** Local date as YYYY-MM-DD — never via toISOString(), which would shift the day in UTC. */
export function toDateInputValue(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function todayValue(): string {
  return toDateInputValue(new Date())
}

export function tomorrowValue(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  return toDateInputValue(date)
}
